"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserService = void 0;
class UserService {
    constructor() {
        this.cache = null;
        /**
         * Gets the user profile data, checking sessionStorage first, then in-memory this.cache, then remote API.
         */
        this.get = async () => {
            const sessionStorageCache = this.getFromSessionStorage();
            if (sessionStorageCache) {
                return { data: sessionStorageCache, errors: [] };
            }
            if (this.cache) {
                return { data: this.cache, errors: [] };
            }
            try {
                const profile = await this.fetchUserProfile();
                this.cache = profile;
                return { data: profile, errors: [] };
            }
            catch (error) {
                return {
                    data: null,
                    errors: [
                        typeof error === 'string'
                            ? error
                            : error instanceof Error
                                ? error.message
                                : 'Unknown error'
                    ]
                };
            }
        };
        /**
         * Fetches user profile data from the remote API and normalizes it.
         * Returns a UserProfileData object.
         */
        this.fetchUserProfile = async () => {
            const location = window.location.href;
            const subdomain = /^(http|https):\/\/www\.ameriprise.com\//.test(location)
                ? 'secure'
                : 'secure-01.qa';
            return fetch(`https://${subdomain}.ameriprise.com/scs.svc/UserProfile?$expand=personalInfo`, {
                mode: 'cors',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'OData-Version': '4.0',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
                .then(response => {
                if (!response.ok) {
                    // eslint-disable-next-line no-throw-literal
                    throw `HTTP error: ${response.status}`;
                }
                return response.json();
            })
                .then(json => {
                // Prepare for extensibility: return all normalized fields here
                const user = Array.isArray(json?.value) && json.value.length > 0
                    ? json.value[0]
                    : {};
                const firstName = user?.personalInfo?.client?.personClient?.clFirstNm ?? null;
                const orgClientArr = user?.personalInfo?.client?.orgClient;
                const isOrgClient = Array.isArray(orgClientArr) && orgClientArr.length > 0;
                return { firstName, isOrgClient };
            })
                .catch(error => {
                // eslint-disable-next-line no-throw-literal
                throw typeof error === 'string'
                    ? error
                    : error instanceof Error
                        ? error.message
                        : 'Unknown error';
            });
        };
        /**
         * Clears the in-memory user profile this.cache.
         */
        this.clearCache = () => {
            this.cache = null;
        };
    }
    static getInstance() {
        if (!UserService.instance) {
            UserService.instance = new UserService();
        }
        return UserService.instance;
    }
    /**
     * Attempts to retrieve user profile data from sessionStorage.
     * Returns null if not found or invalid.
     */
    getFromSessionStorage() {
        const userAccessMeta = sessionStorage.getItem('userAccessMeta');
        if (!userAccessMeta)
            return null;
        let userAccessMetaObj;
        try {
            userAccessMetaObj = JSON.parse(userAccessMeta);
        }
        catch {
            return null;
        }
        if (!this.isValidSessionUserProfile(userAccessMetaObj))
            return null;
        return {
            firstName: userAccessMetaObj.firstName ?? null,
            isOrgClient: !!userAccessMetaObj.isOrgClient
        };
    }
    /**
     * Type guard for validating session user profile object shape.
     */
    isValidSessionUserProfile(obj) {
        return (typeof obj === 'object' &&
            obj !== null &&
            ('firstName' in obj || 'isOrgClient' in obj));
    }
}
exports.UserService = UserService;
